import React, { useState } from 'react';
import { TextField, Button, Typography, Box, Grid, Paper, Link, Stack, InputAdornment, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useAuth } from '../contexts/AuthContext';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, logout } = useAuth();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use the AuthContext login method instead of direct fetch
      const result = await login(email, password);

      if (result.success) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('Login successful, role:', userData.role);
          
          if (userData.role === 'super_admin') {
            navigate('/super-admin');
          } else {
            navigate('/');
          }
        } else {
          console.log('No user data found in localStorage');
          setError('Login failed - user data not stored');
        }
      } else {
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('There was an error logging in!', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();

  return (
    <div className="flex flex-col md:flex-row h-screen">
      {/* Left Info Column */}
      <div className="flex-1 bg-gradient-to-br from-purple-700 to-blue-600 flex items-center justify-center p-4 md:w-1/2 h-1/2 md:h-full">
        <div className="p-4 text-white text-center bg-white bg-opacity-10 backdrop-blur-md rounded-2xl border border-white border-opacity-20">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Intuit LMS
          </Typography>
          <Typography variant="h6" className="opacity-90">
            Innovate Learning. Empower Education.
          </Typography>
        </div>
      </div>

      {/* Right Login Form Column */}
      <Grid item xs={12} md={6} component={Paper} elevation={6} square className="flex-1 flex items-center justify-center">
        <Box
          className="my-4 md:my-8 mx-4 flex flex-col items-center justify-center h-full md:h-[calc(100%-128px)]"
        >
          <Typography component="h1" variant="h4" fontWeight="bold">
            Admin Login
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} className="mt-3 w-full max-w-sm">
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment>,
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              className="mt-3 mb-2 py-1.5 text-lg font-bold rounded-lg bg-gradient-to-r from-pink-500 to-orange-400 transition-all duration-200 hover:translate-y-[-2px] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging In...' : 'Log In'}
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link href="/register" variant="body2">
                  Don't have an account? Register
                </Link>
              </Grid>
            </Grid>
            <Stack spacing={2} className="mt-3">
              <Button variant="outlined" fullWidth startIcon={<GoogleIcon />}>
                Sign in with Google
              </Button>
              <Button variant="outlined" fullWidth startIcon={<FacebookIcon />}>
                Sign in with Facebook
              </Button>
            </Stack>
          </Box>
        </Box>
      </Grid>
    </div>
  );
}

export default Login;
